import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppState, AppAction, CoParentingLink, SharedChild, CustodySwapRequest, SharedExpense, HandoffNote, CustodyPattern, CustodyConfig, ChildMedication, ChildActivity } from '@/types';
import { useCoParenting } from '../contexts/CoParentingContext';
import {
  Heart, ChevronLeft, ChevronRight, Plus, Trash2, Edit2, Check, X,
  ArrowLeftRight, DollarSign, MessageCircle, AlertCircle, Loader2,
  CalendarDays, Users, RefreshCw, Copy, Download, Shield, Send,
} from 'lucide-react';
import { TabBar, TabItem } from '@/components/ui/TabBar';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { collection, addDoc, onSnapshot, orderBy, query, Timestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { ToneMeter } from '@/components/co-parenting/ToneMeter';
import { ExportModal } from '@/components/co-parenting/ExportModal';
import { ConflictResolutionModal } from '@/components/co-parenting/ConflictResolutionModal';
import type { CoParentingMessage, MediatorMember } from '@/types/coparenting';

// ─── Custody schedule engine ──────────────────────────────────────────────────

export function getCustodyDay(date: Date, link: CoParentingLink): string | null {
  if (!link.householdB) return null;
  const dateStr = date.toISOString().split('T')[0];
  const { custodyConfig, custodyPattern } = link;

  // Holiday overrides take priority
  const override = custodyConfig.holidayOverrides?.find(h => h.date === dateStr);
  if (override) return override.householdId;

  const hA = link.householdA.householdId;
  const hB = link.householdB.householdId;

  const daysBetween = (a: Date, b: Date) =>
    Math.floor((b.getTime() - a.getTime()) / 86_400_000);

  if (custodyPattern === 'week-on-week-off' && custodyConfig.weekOnWeekOff) {
    const { startDate, firstHouseholdId } = custodyConfig.weekOnWeekOff;
    const diff = daysBetween(new Date(startDate), date);
    const week = Math.floor(diff / 7);
    return (week % 2 === 0) === (firstHouseholdId === hA) ? hA : hB;
  }

  if (custodyPattern === '2-2-3' && custodyConfig.twoTwoThree) {
    const diff = daysBetween(new Date(custodyConfig.twoTwoThree.anchorDate), date);
    // 14-day cycle: A:0-1, B:2-3, A:4-6, B:7-8, A:9-10, B:11-13
    const pos = ((diff % 14) + 14) % 14;
    return [0, 1, 4, 5, 6, 9, 10].includes(pos) ? hA : hB;
  }

  if (custodyPattern === '5-2-2-5' && custodyConfig.fiveTwoTwoFive) {
    const { anchorDate, firstHouseholdId } = custodyConfig.fiveTwoTwoFive;
    const diff = daysBetween(new Date(anchorDate), date);
    // 14-day cycle: first:0-4,9-13  second:5-8
    const pos = ((diff % 14) + 14) % 14;
    const firstDays = [0, 1, 2, 3, 4, 9, 10, 11, 12, 13];
    const isFirst = firstDays.includes(pos);
    return isFirst === (firstHouseholdId === hA) ? hA : hB;
  }

  if (custodyPattern === 'custom' && custodyConfig.custom) {
    const range = custodyConfig.custom.find(r => r.startDate <= dateStr && dateStr <= r.endDate);
    return range?.householdId ?? null;
  }

  return null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

type Tab = 'schedule' | 'children' | 'swaps' | 'expenses' | 'notes' | 'messages';

// TABS is re-created inside CoParenting component (below) to support t()
const TABS_IDS = ['schedule', 'children', 'swaps', 'expenses', 'notes', 'messages'] as const;

// ─── Setup View ───────────────────────────────────────────────────────────────

function SetupView({ householdId, householdName }: { householdId: string; householdName: string }) {
  const { callCoParenting } = useCoParenting();
  const { t } = useTranslation();
  const [mode, setMode] = useState<'choose' | 'invite' | 'join'>('choose');
  const [loading, setLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [pattern, setPattern] = useState<CustodyPattern>('week-on-week-off');
  const [anchorDate, setAnchorDate] = useState(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleInitiate = async () => {
    setLoading(true); setError('');
    try {
      const config: CustodyConfig = pattern === 'week-on-week-off'
        ? { weekOnWeekOff: { startDate: anchorDate, firstHouseholdId: householdId } }
        : pattern === '2-2-3'
        ? { twoTwoThree: { anchorDate } }
        : pattern === '5-2-2-5'
        ? { fiveTwoTwoFive: { anchorDate, firstHouseholdId: householdId } }
        : { custom: [] };
      const res = await callCoParenting('initiateLink', {
        householdId, householdLabel: householdName, custodyPattern: pattern, custodyConfig: config,
      }) as { inviteCode: string };
      setGeneratedCode(res.inviteCode);
    } catch (e: any) { setError(e.message ?? 'Failed to create link.'); }
    finally { setLoading(false); }
  };

  const handleJoin = async () => {
    if (inviteCode.length !== 6) { setError('Enter a 6-digit code.'); return; }
    setLoading(true); setError('');
    try {
      await callCoParenting('acceptLink', { inviteCode, householdId, householdLabel: householdName });
    } catch (e: any) { setError(e.message ?? 'Failed to join.'); }
    finally { setLoading(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  if (mode === 'choose') return (
    <div className="max-w-md mx-auto py-16 px-4 text-center space-y-6">
      <div className="w-16 h-16 rounded-2xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mx-auto">
        <Heart className="w-8 h-8 text-rose-500" />
      </div>
      <div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white">{t('coparenting.title', 'Co-Parenting')}</h2>
        <p className="text-slate-500 dark:text-slate-400 mt-2">{t('coparenting.setup_desc', 'Link with another household to share custody schedules and child information.')}</p>
      </div>
      <div className="space-y-3">
        <button onClick={() => setMode('invite')} className="w-full py-3 px-6 bg-brand text-white font-bold rounded-2xl hover:bg-[#4a74e8] transition-all">
          {t('coparenting.setup_invite', 'Set up & invite co-parent')}
        </button>
        <button onClick={() => setMode('join')} className="w-full py-3 px-6 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all">
          {t('coparenting.join_invite', 'Join with invite code')}
        </button>
      </div>
    </div>
  );

  if (mode === 'invite') return (
    <div className="max-w-md mx-auto py-12 px-4 space-y-6">
      <button onClick={() => setMode('choose')} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm font-bold">
        <ChevronLeft className="w-4 h-4" /> {t('common.back', 'Back')}
      </button>
      <h2 className="text-xl font-black text-slate-900 dark:text-white">{t('coparenting.choose_schedule', 'Choose a custody schedule')}</h2>

      {!generatedCode ? (
        <div className="space-y-4">
          {([
            { value: 'week-on-week-off', label: 'Week on / Week off', desc: '7-day alternating' },
            { value: '2-2-3',           label: '2-2-3 Rotating',      desc: '14-day cycle' },
            { value: '5-2-2-5',         label: '5-2-2-5 Rotating',    desc: '14-day cycle' },
            { value: 'custom',          label: 'Custom',               desc: 'Set specific date ranges' },
          ] as const).map(opt => (
            <button key={opt.value} onClick={() => setPattern(opt.value)}
              className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${pattern === opt.value ? 'border-brand bg-brand/5' : 'border-slate-200 dark:border-slate-700'}`}>
              <p className="font-bold text-slate-900 dark:text-white">{opt.label}</p>
              <p className="text-sm text-slate-500">{opt.desc}</p>
            </button>
          ))}

          {pattern !== 'custom' && (
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">
                {pattern === 'week-on-week-off' ? 'First custody start date (your household)' : 'Schedule anchor date'}
              </label>
              <input type="date" value={anchorDate} onChange={e => setAnchorDate(e.target.value)}
                className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
            </div>
          )}

          {error && <p className="text-rose-500 text-sm font-bold">{error}</p>}
          <button onClick={handleInitiate} disabled={loading}
            className="w-full py-3 bg-brand text-white font-bold rounded-2xl hover:bg-[#4a74e8] disabled:opacity-50 flex items-center justify-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('coparenting.generate_code', 'Generate invite code')}
          </button>
        </div>
      ) : (
        <div className="text-center space-y-4">
          <p className="text-slate-600 dark:text-slate-400">{t('coparenting.share_code_hint', 'Share this 6-digit code with your co-parent. It expires in 24 hours.')}</p>
          <div className="flex items-center justify-center gap-3 bg-slate-50 dark:bg-slate-800 rounded-2xl p-6">
            <span className="text-4xl font-black tracking-widest text-slate-900 dark:text-white">{generatedCode}</span>
            <button onClick={copyCode} className="p-2 rounded-xl bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600">
              {copied ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5 text-slate-500" />}
            </button>
          </div>
          <p className="text-sm text-slate-400">{t('coparenting.waiting_accept', 'Waiting for co-parent to accept...')}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-md mx-auto py-12 px-4 space-y-6">
      <button onClick={() => setMode('choose')} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm font-bold">
        <ChevronLeft className="w-4 h-4" /> {t('common.back', 'Back')}
      </button>
      <h2 className="text-xl font-black text-slate-900 dark:text-white">{t('coparenting.enter_code', 'Enter invite code')}</h2>
      <input
        type="text" maxLength={6} value={inviteCode}
        onChange={e => setInviteCode(e.target.value.replace(/\D/g, ''))}
        placeholder={t('coparenting.six_digit_placeholder', '6-digit code')}
        className="w-full text-center text-3xl font-black tracking-widest border-2 border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-4 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:border-brand outline-none"
      />
      {error && <p className="text-rose-500 text-sm font-bold">{error}</p>}
      <button onClick={handleJoin} disabled={loading || inviteCode.length !== 6}
        className="w-full py-3 bg-brand text-white font-bold rounded-2xl hover:bg-[#4a74e8] disabled:opacity-50 flex items-center justify-center gap-2">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {t('coparenting.join', 'Join')}
      </button>
    </div>
  );
}

// ─── Schedule Tab ─────────────────────────────────────────────────────────────

function ScheduleTab() {
  const { link, myHouseholdId, otherHouseholdLabel, callCoParenting } = useCoParenting();
  const { t } = useTranslation();
  const [viewDate, setViewDate] = useState(new Date());
  const [overrideDay, setOverrideDay] = useState<string | null>(null);
  const [overrideName, setOverrideName] = useState('');
  const [overrideHousehold, setOverrideHousehold] = useState('');

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const monthLabel = viewDate.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });

  const myLabel = link?.householdA.householdId === myHouseholdId ? link.householdA.label : link?.householdB?.label ?? 'My Household';
  const otherLabel = otherHouseholdLabel;

  const getCustodyLabel = (hId: string | null) => {
    if (!hId) return '';
    return hId === myHouseholdId ? 'mine' : 'other';
  };

  const handleAddOverride = async () => {
    if (!overrideDay || !overrideName || !overrideHousehold || !link) return;
    const overrides = link.custodyConfig.holidayOverrides ?? [];
    overrides.push({ id: `override-${Date.now()}`, name: overrideName, date: overrideDay, householdId: overrideHousehold });
    await callCoParenting('updateCustodyConfig', {
      linkId: link.id,
      custodyPattern: link.custodyPattern,
      custodyConfig: { ...link.custodyConfig, holidayOverrides: overrides },
    });
    setOverrideDay(null); setOverrideName(''); setOverrideHousehold('');
  };

  if (!link) return null;

  return (
    <div className="space-y-4">
      {/* Month nav */}
      <div className="flex items-center justify-between">
        <button onClick={() => setViewDate(new Date(year, month - 1, 1))} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="font-black text-slate-900 dark:text-white">{monthLabel}</span>
        <button onClick={() => setViewDate(new Date(year, month + 1, 1))} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-sm font-bold">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-brand" />{myLabel}</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-violet-500" />{otherLabel}</span>
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
          <div key={d} className="text-center text-xs font-black text-slate-400 py-1">{d}</div>
        ))}
        {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const d = new Date(year, month, day);
          const hId = getCustodyDay(d, link);
          const who = getCustodyLabel(hId);
          const prevDay = new Date(year, month, day - 1);
          const prevHId = day > 1 ? getCustodyDay(prevDay, link) : null;
          const isHandoff = day > 1 && prevHId !== null && hId !== null && prevHId !== hId;
          const today = new Date(); const isToday = d.toDateString() === today.toDateString();
          return (
            <button key={day} onClick={() => { setOverrideDay(d.toISOString().split('T')[0]); setOverrideHousehold(hId ?? myHouseholdId); }}
              className={`relative aspect-square flex flex-col items-center justify-center rounded-xl text-sm font-bold transition-all hover:opacity-80 ${
                who === 'mine' ? 'bg-brand/20 text-brand' :
                who === 'other' ? 'bg-violet-500/20 text-violet-600 dark:text-violet-400' :
                'bg-slate-100 dark:bg-slate-800 text-slate-400'
              } ${isToday ? 'ring-2 ring-offset-1 ring-brand' : ''}`}>
              {day}
              {isHandoff && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-amber-400" />}
            </button>
          );
        })}
      </div>

      {/* Override modal */}
      {overrideDay && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 w-full max-w-sm space-y-4">
            <h3 className="font-black text-slate-900 dark:text-white">{t('coparenting.override_title', 'Override')}: {overrideDay}</h3>
            <input placeholder={t('coparenting.override_label_placeholder', 'Label (e.g. Christmas)')} value={overrideName} onChange={e => setOverrideName(e.target.value)}
              className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
            <div className="flex gap-2">
              {[link.householdA, link.householdB].filter(Boolean).map(h => h && (
                <button key={h.householdId} onClick={() => setOverrideHousehold(h.householdId)}
                  className={`flex-1 py-2 rounded-xl font-bold text-sm transition-all ${overrideHousehold === h.householdId ? 'bg-brand text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}>
                  {h.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setOverrideDay(null)} className="flex-1 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 font-bold text-slate-600 dark:text-slate-300">{t('common.cancel', 'Cancel')}</button>
              <button onClick={handleAddOverride} className="flex-1 py-2 rounded-xl bg-brand text-white font-bold">{t('common.save', 'Save')}</button>
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400 text-center">{t('coparenting.calendar_hint', 'Tap any day to override custody assignment. Amber dot = handoff day.')}</p>
    </div>
  );
}

// ─── Children Tab ─────────────────────────────────────────────────────────────

function ChildrenTab() {
  const { link, children, myHouseholdId, callCoParenting } = useCoParenting();
  const { t } = useTranslation();
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const blankChild = { firstName: '', lastName: '', dateOfBirth: '', schoolName: '', allergies: [] as string[], medications: [] as ChildMedication[], activities: [] as ChildActivity[], emergencyContacts: [] as Array<{ id: string; name: string; relation: string; phone: string }> };
  const [form, setForm] = useState<typeof blankChild>(blankChild);

  const calcAge = (dob: string) => {
    const diff = Date.now() - new Date(dob).getTime();
    return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
  };

  const handleSave = async () => {
    if (!link) return;
    setSaving(true);
    try {
      if (editId) {
        await callCoParenting('updateChild', { linkId: link.id, childId: editId, updates: form });
      } else {
        await callCoParenting('addChild', { linkId: link.id, child: form });
      }
      setShowAdd(false); setEditId(null); setForm(blankChild);
    } finally { setSaving(false); }
  };

  const handleRequestDelete = async (childId: string) => {
    if (!link || !confirm('Request deletion of this child profile? The other household must confirm.')) return;
    await callCoParenting('requestChildDeletion', { linkId: link.id, childId });
  };

  const handleConfirmDelete = async (childId: string) => {
    if (!link || !confirm('Permanently delete this child profile? This cannot be undone.')) return;
    await callCoParenting('confirmChildDeletion', { linkId: link.id, childId });
  };

  const handleCancelDelete = async (childId: string) => {
    if (!link) return;
    await callCoParenting('cancelChildDeletion', { linkId: link.id, childId });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-black text-slate-900 dark:text-white">{t('coparenting.children_title', 'Children')}</h3>
        <button onClick={() => { setShowAdd(true); setEditId(null); setForm(blankChild); }}
          className="flex items-center gap-2 px-4 py-2 bg-brand text-white font-bold rounded-xl text-sm hover:bg-[#4a74e8]">
          <Plus className="w-4 h-4" /> {t('coparenting.add_child', 'Add Child')}
        </button>
      </div>

      {children.length === 0 && !showAdd && (
        <div className="text-center py-12 text-slate-400">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="font-bold">{t('coparenting.no_children', 'No children added yet')}</p>
        </div>
      )}

      {children.map(child => (
        <div key={child.id} className={`rounded-2xl border p-4 space-y-2 ${child.pendingDeletion ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/10' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
          <div className="flex items-start justify-between">
            <div>
              <p className="font-black text-slate-900 dark:text-white">{child.firstName} {child.lastName}</p>
              {child.dateOfBirth && <p className="text-sm text-slate-500">Age {calcAge(child.dateOfBirth)}{child.schoolName ? ` · ${child.schoolName}` : ''}</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditId(child.id); setForm({ ...blankChild, ...child }); setShowAdd(true); }}
                className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600">
                <Edit2 className="w-4 h-4" />
              </button>
              {!child.pendingDeletion && (
                <button onClick={() => handleRequestDelete(child.id)}
                  className="p-2 rounded-xl hover:bg-rose-50 dark:hover:bg-rose-900/20 text-slate-400 hover:text-rose-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {child.allergies.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {child.allergies.map(a => <span key={a} className="px-2 py-0.5 bg-rose-100 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 text-xs font-bold rounded-full">{a}</span>)}
            </div>
          )}
          {child.medications.length > 0 && (
            <p className="text-sm text-slate-500">{child.medications.length} medication{child.medications.length > 1 ? 's' : ''}</p>
          )}

          {/* Pending deletion banner */}
          {child.pendingDeletion && (
            <div className="pt-2 border-t border-amber-200 dark:border-amber-700">
              {child.pendingDeletion.requestedBy === myHouseholdId ? (
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-amber-700 dark:text-amber-400">Awaiting co-parent confirmation to delete</p>
                  <button onClick={() => handleCancelDelete(child.id)} className="text-sm font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-amber-700 dark:text-amber-400">Co-parent requested deletion</p>
                  <button onClick={() => handleConfirmDelete(child.id)} className="px-3 py-1 bg-rose-500 text-white text-sm font-bold rounded-lg hover:bg-rose-600">
                    Confirm Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Add/Edit form */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 w-full max-w-md space-y-4">
            <h3 className="font-black text-slate-900 dark:text-white">{editId ? t('coparenting.edit_child', 'Edit Child') : t('coparenting.add_child', 'Add Child')}</h3>
            {[
              { label: 'First Name', key: 'firstName' }, { label: 'Last Name', key: 'lastName' },
              { label: 'School', key: 'schoolName' },
            ].map(({ label, key }) => (
              <div key={key}>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">{label}</label>
                <input value={(form as any)[key] ?? ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
              </div>
            ))}
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Date of Birth</label>
              <input type="date" value={form.dateOfBirth ?? ''} onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))}
                className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Allergies (comma-separated)</label>
              <input value={form.allergies.join(', ')} onChange={e => setForm(f => ({ ...f, allergies: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => { setShowAdd(false); setEditId(null); }} className="flex-1 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 font-bold text-slate-600 dark:text-slate-300">{t('common.cancel', 'Cancel')}</button>
              <button onClick={handleSave} disabled={saving || !form.firstName.trim()}
                className="flex-1 py-2.5 rounded-xl bg-brand text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} {t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Swaps Tab ────────────────────────────────────────────────────────────────

function SwapsTab() {
  const { link, swapRequests, myHouseholdId, callCoParenting } = useCoParenting();
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [originalDate, setOriginalDate] = useState('');
  const [proposedDate, setProposedDate] = useState('');
  const [oneWay, setOneWay] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [counterDate, setCounterDate] = useState('');
  const [counteringId, setCounteringId] = useState<string | null>(null);

  const incoming = swapRequests.filter(s => s.requestedBy !== myHouseholdId && s.status === 'pending');
  const countered = swapRequests.filter(s => s.requestedBy !== myHouseholdId && s.status === 'countered');
  const outgoing = swapRequests.filter(s => s.requestedBy === myHouseholdId && ['pending','countered'].includes(s.status));
  const resolved = swapRequests.filter(s => ['accepted','declined','withdrawn'].includes(s.status)).slice(0, 10);

  const handleSubmit = async () => {
    if (!link || !originalDate) return;
    setSaving(true);
    try {
      await callCoParenting('requestSwap', { linkId: link.id, originalDate, proposedDate: oneWay ? undefined : proposedDate, oneWay, reason });
      setShowForm(false); setOriginalDate(''); setProposedDate(''); setReason(''); setOneWay(false);
    } finally { setSaving(false); }
  };

  const respond = async (swapId: string, response: string, extra?: object) => {
    if (!link) return;
    await callCoParenting('respondToSwap', { linkId: link.id, swapId, response, ...extra });
    setCounteringId(null); setCounterDate('');
  };

  const statusBadge = (s: string) => ({
    pending: 'bg-amber-100 text-amber-700',
    accepted: 'bg-emerald-100 text-emerald-700',
    declined: 'bg-rose-100 text-rose-700',
    countered: 'bg-violet-100 text-violet-700',
    withdrawn: 'bg-slate-100 text-slate-500',
  }[s] ?? 'bg-slate-100 text-slate-500');

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-black text-slate-900 dark:text-white">{t('coparenting.swaps_title', 'Custody Swaps')}</h3>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-brand text-white font-bold rounded-xl text-sm hover:bg-[#4a74e8]">
          <Plus className="w-4 h-4" /> {t('coparenting.request_swap', 'Request Swap')}
        </button>
      </div>

      {[...incoming, ...countered].length > 0 && (
        <section>
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Action Required</p>
          {[...incoming, ...countered].map(s => (
            <div key={s.id} className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-2xl p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-bold text-slate-900 dark:text-white">Give up: <span className="text-brand">{s.originalDate}</span></p>
                  {!s.oneWay && s.proposedDate && <p className="text-sm text-slate-500">In return: {counteringId === s.id ? counterDate : s.proposedDate}</p>}
                  {s.status === 'countered' && s.counterProposedDate && <p className="text-sm font-bold text-violet-600">Counter offer: {s.counterProposedDate}</p>}
                  {s.reason && <p className="text-sm text-slate-500 mt-1">"{s.reason}"</p>}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusBadge(s.status)}`}>{s.status}</span>
              </div>
              {counteringId === s.id ? (
                <div className="flex gap-2">
                  <input type="date" value={counterDate} onChange={e => setCounterDate(e.target.value)}
                    className="flex-1 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm" />
                  <button onClick={() => respond(s.id, 'countered', { counterProposedDate: counterDate })} className="px-3 py-2 bg-violet-500 text-white font-bold rounded-xl text-sm">Send</button>
                  <button onClick={() => setCounteringId(null)} className="px-3 py-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-xl text-sm">Cancel</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => respond(s.id, 'accepted')} className="px-3 py-2 bg-emerald-500 text-white font-bold rounded-xl text-sm hover:bg-emerald-600">Accept</button>
                  <button onClick={() => setCounteringId(s.id)} className="px-3 py-2 bg-violet-100 text-violet-700 font-bold rounded-xl text-sm hover:bg-violet-200">Counter</button>
                  <button onClick={() => respond(s.id, 'declined')} className="px-3 py-2 bg-rose-100 text-rose-600 font-bold rounded-xl text-sm hover:bg-rose-200">Decline</button>
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {outgoing.length > 0 && (
        <section>
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Your Requests</p>
          {outgoing.map(s => (
            <div key={s.id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 flex items-center justify-between">
              <div>
                <p className="font-bold text-slate-900 dark:text-white">{s.originalDate}{!s.oneWay && s.proposedDate ? ` ↔ ${s.proposedDate}` : ' (one-way)'}</p>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusBadge(s.status)}`}>{s.status}</span>
              </div>
              <button onClick={() => callCoParenting('withdrawSwap', { linkId: link?.id, swapId: s.id })}
                className="p-2 rounded-xl text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </section>
      )}

      {resolved.length > 0 && (
        <section>
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Recent History</p>
          {resolved.map(s => (
            <div key={s.id} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <p className="text-sm text-slate-600 dark:text-slate-400">{s.originalDate}{s.proposedDate ? ` ↔ ${s.proposedDate}` : ''}</p>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusBadge(s.status)}`}>{s.status}</span>
            </div>
          ))}
        </section>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 w-full max-w-sm space-y-4">
            <h3 className="font-black text-slate-900 dark:text-white">{t('coparenting.request_swap', 'Request Custody Swap')}</h3>
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Day I want to give up</label>
              <input type="date" value={originalDate} onChange={e => setOriginalDate(e.target.value)}
                className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
            </div>
            <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
              <input type="checkbox" checked={oneWay} onChange={e => setOneWay(e.target.checked)} className="rounded" />
              One-way (no return day)
            </label>
            {!oneWay && (
              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Day I'm offering in return</label>
                <input type="date" value={proposedDate} onChange={e => setProposedDate(e.target.value)}
                  className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
              </div>
            )}
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Reason (optional)</label>
              <input value={reason} onChange={e => setReason(e.target.value)}
                className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 font-bold text-slate-600 dark:text-slate-300">Cancel</button>
              <button onClick={handleSubmit} disabled={saving || !originalDate}
                className="flex-1 py-2.5 rounded-xl bg-brand text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Expenses Tab ─────────────────────────────────────────────────────────────

function ExpensesTab() {
  const { link, expenses, children, myHouseholdId, callCoParenting } = useCoParenting();
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ childId: 'all', description: '', amount: '', currency: 'CAD', category: 'other', date: new Date().toISOString().split('T')[0], receiptPhotoUrl: '' });

  const avgMonthly = useMemo(() => {
    if (expenses.length === 0) return 0;
    const months = new Set(expenses.map(e => e.date.slice(0, 7)));
    const total = expenses.reduce((s, e) => s + e.amount, 0);
    return total / months.size;
  }, [expenses]);

  const handleAdd = async () => {
    if (!link || !form.description.trim() || !form.amount) return;
    setSaving(true);
    try {
      const expense = {
        ...form,
        amount: parseFloat(form.amount),
        receiptPhotoUrl: form.receiptPhotoUrl.trim() || undefined,
        submitted: true,
      };
      await callCoParenting('addExpense', { linkId: link.id, expense });
      setShowForm(false);
      setForm({ childId: 'all', description: '', amount: '', currency: 'CAD', category: 'other', date: new Date().toISOString().split('T')[0], receiptPhotoUrl: '' });
    } finally { setSaving(false); }
  };

  const CATS = ['medical','education','clothing','activity','other'];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-black text-slate-900 dark:text-white">{t('coparenting.expenses_title', 'Shared Expenses')}</h3>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-brand text-white font-bold rounded-xl text-sm hover:bg-[#4a74e8]">
          <Plus className="w-4 h-4" /> {t('common.add', 'Add')}
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-4">
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Total</p>
          <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">
            ${expenses.reduce((s, e) => s + e.amount, 0).toFixed(2)}
          </p>
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-4">
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Avg / Month</p>
          <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">${avgMonthly.toFixed(2)}</p>
        </div>
      </div>

      {/* Expense list */}
      {expenses.length === 0 ? (
        <div className="text-center py-10 text-slate-400">
          <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="font-bold">{t('coparenting.no_expenses', 'No expenses logged yet')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {[...expenses].sort((a, b) => b.date.localeCompare(a.date)).map(e => {
            const child = e.childId !== 'all' ? children.find(c => c.id === e.childId) : null;
            const isMine = e.paidBy === myHouseholdId;
            return (
              <div key={e.id} className="flex items-center justify-between bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-3">
                <div>
                  <p className="font-bold text-slate-900 dark:text-white text-sm">{e.description}</p>
                  <p className="text-xs text-slate-400">{e.date} · {e.category}{child ? ` · ${child.firstName}` : ''}</p>
                </div>
                <div className="text-right">
                  <p className={`font-black text-sm ${isMine ? 'text-brand' : 'text-violet-500'}`}>${e.amount.toFixed(2)}</p>
                  <p className="text-xs text-slate-400">{isMine ? 'You' : 'Co-parent'}</p>
                  {e.receiptUrl && (
                    <a href={e.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-brand underline underline-offset-1 mt-0.5 block">
                      View Receipt
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 w-full max-w-sm space-y-4">
            <h3 className="font-black text-slate-900 dark:text-white">{t('coparenting.add_expense', 'Add Expense')}</h3>
            <input placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
            <div className="flex gap-2">
              <input type="number" placeholder="Amount" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                className="flex-1 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                className="w-24 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white">
                {['CAD','USD','EUR','GBP'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white">
              {CATS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
            <select value={form.childId} onChange={e => setForm(f => ({ ...f, childId: e.target.value }))}
              className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white">
              <option value="all">{t('coparenting.all_children', 'All children')}</option>
              {children.map(c => <option key={c.id} value={c.id}>{c.firstName}</option>)}
            </select>
            <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
            <input
              type="url"
              placeholder="Receipt URL (optional)"
              value={form.receiptPhotoUrl}
              onChange={e => setForm(f => ({ ...f, receiptPhotoUrl: e.target.value }))}
              className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            />
            <div className="flex gap-2">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 font-bold text-slate-600 dark:text-slate-300">Cancel</button>
              <button onClick={handleAdd} disabled={saving || !form.description.trim() || !form.amount}
                className="flex-1 py-2.5 rounded-xl bg-brand text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Notes Tab ────────────────────────────────────────────────────────────────

function NotesTab() {
  const { link, handoffNotes, children, myHouseholdId, callCoParenting } = useCoParenting();
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ childId: '', message: '', isUrgent: false, handoffDate: new Date().toISOString().split('T')[0] });

  const sorted = [...handoffNotes].sort((a, b) => b.handoffDate.localeCompare(a.handoffDate));

  const handleAdd = async () => {
    if (!link || !form.message.trim()) return;
    setSaving(true);
    try {
      await callCoParenting('addHandoffNote', { linkId: link.id, ...form, childId: form.childId || undefined });
      setShowForm(false); setForm({ childId: '', message: '', isUrgent: false, handoffDate: new Date().toISOString().split('T')[0] });
    } finally { setSaving(false); }
  };

  const handleMarkRead = async (noteId: string) => {
    if (!link) return;
    await callCoParenting('markNoteRead', { linkId: link.id, noteId });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-black text-slate-900 dark:text-white">{t('coparenting.notes_title', 'Handoff Notes')}</h3>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-brand text-white font-bold rounded-xl text-sm hover:bg-[#4a74e8]">
          <Plus className="w-4 h-4" /> {t('coparenting.add_note', 'Add Note')}
        </button>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-10 text-slate-400">
          <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="font-bold">{t('coparenting.no_notes', 'No handoff notes yet')}</p>
        </div>
      ) : (
        sorted.map(note => {
          const isUnread = !note.readBy.includes(myHouseholdId);
          const child = note.childId ? children.find(c => c.id === note.childId) : null;
          const isFromMe = note.fromHouseholdId === myHouseholdId;
          return (
            <div key={note.id}
              onClick={() => isUnread && handleMarkRead(note.id)}
              className={`rounded-2xl p-4 border transition-all cursor-pointer ${
                note.isUrgent ? 'border-rose-300 bg-rose-50 dark:bg-rose-900/10' :
                isUnread ? 'border-brand/40 bg-brand/5' :
                'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'
              }`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {note.isUrgent && <AlertCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />}
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{note.handoffDate}</span>
                    {child && <span className="text-xs font-bold text-slate-500">· {child.firstName}</span>}
                    {isUnread && <span className="w-2 h-2 rounded-full bg-brand flex-shrink-0" />}
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-300">{note.message}</p>
                  <p className="text-xs text-slate-400 mt-1">{isFromMe ? 'You' : 'Co-parent'}</p>
                </div>
              </div>
            </div>
          );
        })
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 w-full max-w-sm space-y-4">
            <h3 className="font-black text-slate-900 dark:text-white">{t('coparenting.add_note', 'Add Handoff Note')}</h3>
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Handoff date</label>
              <input type="date" value={form.handoffDate} onChange={e => setForm(f => ({ ...f, handoffDate: e.target.value }))}
                className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
            </div>
            <select value={form.childId} onChange={e => setForm(f => ({ ...f, childId: e.target.value }))}
              className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white">
              <option value="">All children</option>
              {children.map(c => <option key={c.id} value={c.id}>{c.firstName}</option>)}
            </select>
            <textarea placeholder="Message" rows={3} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-none" />
            <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
              <input type="checkbox" checked={form.isUrgent} onChange={e => setForm(f => ({ ...f, isUrgent: e.target.checked }))} className="rounded" />
              Mark as urgent
            </label>
            <div className="flex gap-2">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 font-bold text-slate-600 dark:text-slate-300">Cancel</button>
              <button onClick={handleAdd} disabled={saving || !form.message.trim()}
                className="flex-1 py-2.5 rounded-xl bg-brand text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Messages Tab ─────────────────────────────────────────────────────────────

function MessagesTab({ state }: { state: AppState }) {
  const { link, myHouseholdId } = useCoParenting();
  const [messages, setMessages] = useState<CoParentingMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const currentUser = state.currentUser;
  const householdId = state.currentHouseholdId;

  useEffect(() => {
    if (!link) return;
    const q = query(
      collection(db, 'coParenting', link.id, 'messages'),
      orderBy('clientTimestamp', 'asc')
    );
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() } as CoParentingMessage)));
    });
    return unsub;
  }, [link]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!link || !draft.trim() || !currentUser) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'coParenting', link.id, 'messages'), {
        content: draft.trim(),
        senderId: currentUser.id,
        senderName: currentUser.name || 'You',
        clientTimestamp: Timestamp.now(),
        toneScore: null,
        toneAnalyzed: false,
        edited: false,
      });
      setDraft('');
    } finally {
      setSending(false);
    }
  };

  if (!link) return null;

  return (
    <div className="flex flex-col h-[60vh]">
      {/* Message list */}
      <div className="flex-1 overflow-y-auto space-y-3 pb-3">
        {messages.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="font-bold">No messages yet — start the conversation</p>
          </div>
        )}
        {messages.map(msg => {
          const isMe = msg.senderId === currentUser?.id;
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${isMe ? 'bg-brand text-white' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white'}`}>
                {!isMe && <p className="text-xs font-bold opacity-60 mb-1">{msg.senderName}</p>}
                <p className="text-sm">{msg.content}</p>
                {msg.toneScore && msg.toneScore !== 'neutral' && (
                  <span className={`text-xs mt-1 block opacity-70 ${msg.toneScore === 'accusatory' ? 'text-red-300' : 'text-amber-300'}`}>
                    {msg.toneScore}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      <div className="border-t border-slate-200 dark:border-slate-700 pt-3 space-y-2">
        <div className="relative">
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder="Type a message... (Enter to send)"
            rows={2}
            className="w-full border-2 border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-none focus:outline-none focus:border-brand pr-12"
          />
          <button
            onClick={handleSend}
            disabled={sending || !draft.trim()}
            className="absolute right-2 bottom-2 p-2 rounded-xl bg-brand text-white disabled:opacity-40 hover:bg-[#4a74e8] transition-colors"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <ToneMeter draft={draft} householdId={householdId} onSuggestionApply={s => setDraft(s)} />
      </div>
    </div>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────

export const CoParenting: React.FC<{ state: AppState; dispatch: React.Dispatch<AppAction> }> = ({ state }) => {
  const { link, isLinked, isLoaded, unreadNoteCount, callCoParenting } = useCoParenting();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('schedule');
  const [showExport, setShowExport] = useState(false);
  const [mediators, setMediators] = useState<MediatorMember[]>([]);
  const [showInviteMediator, setShowInviteMediator] = useState(false);
  const [mediatorEmail, setMediatorEmail] = useState('');
  const [invitingMediator, setInvitingMediator] = useState(false);

  const currentUser = state.currentUser;
  const isAdmin = link && (
    link.householdA.householdId === state.currentHouseholdId
  );

  // Subscribe to mediator members
  useEffect(() => {
    if (!link) return;
    const unsub = onSnapshot(
      query(collection(db, 'coParenting', link.id, 'members')),
      snap => {
        const meds = snap.docs
          .map(d => ({ ...d.data() } as MediatorMember))
          .filter(m => m.role === 'mediator');
        setMediators(meds);
      }
    );
    return unsub;
  }, [link]);

  const handleInviteMediator = async () => {
    if (!link || !mediatorEmail.trim() || !currentUser) return;
    setInvitingMediator(true);
    try {
      const fn = httpsCallable(getFunctions(getApp()), 'inviteCoParentingMediator');
      await fn({ linkId: link.id, mediatorEmail: mediatorEmail.trim(), invitedBy: currentUser.id });
      setMediatorEmail('');
      setShowInviteMediator(false);
    } finally {
      setInvitingMediator(false);
    }
  };

  const TABS: TabItem[] = [
    { id: 'schedule',  label: t('coparenting.tab_schedule', 'Schedule'),  icon: <CalendarDays className="w-4 h-4" /> },
    { id: 'children',  label: t('coparenting.tab_children', 'Children'),  icon: <Users className="w-4 h-4" /> },
    { id: 'swaps',     label: t('coparenting.tab_swaps', 'Swaps'),        icon: <ArrowLeftRight className="w-4 h-4" /> },
    { id: 'expenses',  label: t('coparenting.tab_expenses', 'Expenses'),  icon: <DollarSign className="w-4 h-4" /> },
    { id: 'notes',     label: t('coparenting.tab_notes', 'Notes'),        icon: <MessageCircle className="w-4 h-4" /> },
    { id: 'messages',  label: 'Messages',                                 icon: <Send className="w-4 h-4" /> },
  ];

  const household = state.households?.find(h => h.id === state.currentHouseholdId);

  if (!isLoaded) return (
    <div className="flex items-center justify-center h-64">
      <LoadingSpinner />
    </div>
  );

  if (!isLinked) return (
    <SetupView
      householdId={state.currentHouseholdId ?? ''}
      householdName={household?.name ?? 'My Household'}
    />
  );

  return (
    <div className="space-y-6">
      {/* Header actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {mediators.length > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-100 dark:bg-violet-900/20 rounded-full">
              <Shield className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
              <span className="text-xs font-bold text-violet-700 dark:text-violet-300">
                Mediator active
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => setShowInviteMediator(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              <Shield className="w-3.5 h-3.5" />
              {mediators.length === 0 ? 'Add Mediator' : 'Mediators'}
            </button>
          )}
          <button
            onClick={() => setShowExport(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Export Thread
          </button>
        </div>
      </div>

      <TabBar
        tabs={TABS.map(t => ({ ...t, badge: t.id === 'notes' ? unreadNoteCount : undefined }))}
        activeTab={activeTab}
        onChange={id => setActiveTab(id as Tab)}
      />

      {/* Tab content */}
      {activeTab === 'schedule'  && <ScheduleTab />}
      {activeTab === 'children'  && <ChildrenTab />}
      {activeTab === 'swaps'     && <SwapsTab />}
      {activeTab === 'expenses'  && <ExpensesTab />}
      {activeTab === 'notes'     && <NotesTab />}
      {activeTab === 'messages'  && <MessagesTab state={state} />}

      {/* Export Modal */}
      {showExport && link && currentUser && (
        <ExportModal
          linkId={link.id}
          requestingUserId={currentUser.id}
          isOpen={showExport}
          onClose={() => setShowExport(false)}
        />
      )}

      {/* Invite Mediator Modal */}
      {showInviteMediator && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-slate-900 dark:text-white">Invite Mediator</h3>
              <button onClick={() => setShowInviteMediator(false)} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            {mediators.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Current Mediators</p>
                {mediators.map(m => (
                  <div key={m.userId} className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
                    <Shield className="w-4 h-4 text-violet-500" />
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{m.email}</span>
                  </div>
                ))}
              </div>
            )}

            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Mediator Email</label>
              <input
                type="email"
                placeholder="mediator@example.com"
                value={mediatorEmail}
                onChange={e => setMediatorEmail(e.target.value)}
                className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              />
            </div>

            <div className="flex gap-2">
              <button onClick={() => setShowInviteMediator(false)} className="flex-1 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 font-bold text-slate-600 dark:text-slate-300">Cancel</button>
              <button
                onClick={handleInviteMediator}
                disabled={invitingMediator || !mediatorEmail.trim()}
                className="flex-1 py-2.5 rounded-xl bg-brand text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {invitingMediator && <Loader2 className="w-4 h-4 animate-spin" />}
                Invite
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
